import { Stack, Typography } from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import { DataTable } from '../../components/global/Table/DataTable';
import { Link } from '../../components/global/Link';
import { DataTableHeadCellData } from '../../components/global/Table/DataTableHeader';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../hooks/useAPI';
import { SessionData as SessionDataFormat } from '../../types';
import { XlsxIcon } from '../../assets/svgs/XlsxIcon';

const headCells: DataTableHeadCellData[] = [
  {
    id: 'id',
    disablePadding: true,
    label: 'Id',
    align: 'left',
    sortable: true,
    searchable: true,
    width: '200px',
  },
  {
    id: 'name',
    disablePadding: true,
    label: 'Session Name',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'count',
    disablePadding: true,
    label: 'Number of records',
    align: 'left',
    cellAlign: 'left',
  },
  {
    id: 'download',
    disablePadding: false,
    label: 'Download (.xlsx)',
    width: '150px',
  },
];

interface SessionDataTableData {
  id: any;
  name: any;
  count: any;
  download: JSX.Element;
}

export const SessionData = () => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData, token } = useAPI();
  const [rows, setRows] = useState<SessionDataTableData[]>([]);
  const [sessions, setSessions] = useState<SessionDataFormat[]>([]);

  const onDownload = async (id: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/export-session/${id}`,
        {
          method: 'GET',
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            Authorization: `Bearer ${token}`, // if auth is required
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to download session XLSX');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session-${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading session XLSX:', error);
    }
  };

  const getRows = (sessionList: SessionDataFormat[]) => {
    const result: SessionDataTableData[] = [];

    for (let i = 0; i < sessionList.length; i++) {
      result.push({
        id: `${sessionList[i].id}`,
        name: <Typography>{sessionList[i].name}</Typography>,
        count: <Typography>{sessionList[i].count}</Typography>,
        download: (
          <Link to={`#`} onClick={() => onDownload(sessionList[i].id)}>
            <XlsxIcon />
          </Link>
        ),
      });
    }
    return result;
  };

  const refetchSessions = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData('/user-sessions');

    setSessions(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchSessions();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(sessions));
  }, [sessions]);

  return (
    <Frame title="Session Data">
      <Stack>
        <DataTable
          title="Session Data"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
        />
      </Stack>
    </Frame>
  );
};
